// chat/fuzzy.ts — FLEET STANDARD: forgiving name resolution for the in-app brain.
// Users paraphrase, drop a middle word, or mistype the thing they just named
// ("research my 1921 Morgan dollar" for a stored "1921 Morgan Silver Dollar";
// "morgn dollar" for "Morgan Dollar"). Exact-substring matching fails all of these.
// This module ranks candidates by NEAR match — tolerant of misspellings, missing
// words, and word order — the same way a memory recall surfaces the closest hit.
//
// Zero deps, pure + synchronous, so it's trivially copied into every Oriora app's
// chat layer and unit-testable. Kept deliberately conservative: it never invents a
// match out of thin air (every query token must have a near-token in the name), so
// a wrong item is only ever *offered*, and destructive actions (delete) still confirm.

/** Lowercase, strip diacritics + punctuation, collapse whitespace. */
export function norm(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // drop combining accents
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Filler words that carry no identity — dropped from the QUERY so "my 1921 morgan
// coin" matches "1921 Morgan Silver Dollar". Kept tiny + domain-neutral on purpose
// (never drop words like "half"/"dollar"/"silver" that distinguish real items).
const STOP = new Set(["a", "an", "the", "my", "of", "and", "to", "for", "in", "on", "this", "that", "it", "please", "me"]);

export function tokens(normalized: string): string[] {
  return normalized.split(" ").filter((t) => t.length > 1 && !STOP.has(t));
}

/** Bounded Levenshtein edit distance (early-exit once it exceeds `max`). */
export function levenshtein(a: string, b: string, max = 3): number {
  if (a === b) return 0;
  const al = a.length, bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  let prev = Array.from({ length: bl + 1 }, (_, i) => i);
  let curr = new Array(bl + 1).fill(0);
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1; // whole row already past budget
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

/** Edit-distance tolerance scaled to the query token's length. */
function tol(len: number): number {
  return len <= 3 ? 0 : len <= 4 ? 1 : len <= 7 ? 2 : 3;
}

/** Does query token `q` have a near-match among a name's tokens? Returns the edit
 *  distance of the best match, or null if none is close enough. */
function nearestTokenDist(q: string, nameTokens: string[]): number | null {
  let best: number | null = null;
  for (const n of nameTokens) {
    if (q === n) return 0;
    // prefix match ("morg" ~ "morgan", "kennedy" ~ "kenn") for length ≥ 4
    if ((q.length >= 4 && n.startsWith(q)) || (n.length >= 4 && q.startsWith(n))) {
      best = best === null ? 1 : Math.min(best, 1);
      continue;
    }
    const d = levenshtein(q, n, tol(q.length));
    if (d <= tol(q.length) && (best === null || d < best)) best = d;
  }
  return best;
}

export interface Named { name: string }

interface Scored<T> { item: T; matched: number; total: number; dist: number; substring: boolean }

function scoreAll<T extends Named>(items: T[], term: string): Scored<T>[] {
  const qn = norm(term);
  if (!qn) return [];
  const qTokens = tokens(qn);
  return items.map((item) => {
    const nn = norm(item.name);
    const nTokens = nn.split(" ").filter(Boolean);
    let matched = 0, dist = 0;
    for (const q of qTokens) {
      const d = nearestTokenDist(q, nTokens);
      if (d !== null) { matched++; dist += d; }
    }
    return { item, matched, total: qTokens.length, dist, substring: nn.includes(qn) };
  });
}

/**
 * Confident matches for `term` within `items`, mirroring the old contract
 * (0 = none, 1 = a clear winner, >1 = genuinely ambiguous → caller disambiguates).
 * Order of precedence: exact substring → all query tokens near-matched (closest wins).
 */
export function findNamedMatches<T extends Named>(items: T[], term: string): T[] {
  const qn = norm(term);
  if (!qn) return [];
  // 1) exact substring — precise, fast path (unchanged behaviour when it works)
  const subs = items.filter((i) => norm(i.name).includes(qn));
  if (subs.length) return subs;
  // 2) fuzzy: keep only items where EVERY query token found a near token
  const scored = scoreAll(items, term).filter((s) => s.total > 0 && s.matched === s.total);
  if (!scored.length) return [];
  scored.sort((a, b) => a.dist - b.dist);
  const bestDist = scored[0].dist;
  return scored.filter((s) => s.dist === bestDist).map((s) => s.item);
}

/**
 * Closest names to offer when there is NO confident match — powers a helpful
 * "did you mean …?" instead of a dead "Couldn't find X". Returns up to `n` names,
 * best first, requiring at least one near-token so we never suggest nonsense.
 */
export function suggestNames<T extends Named>(items: T[], term: string, n = 3): string[] {
  return scoreAll(items, term)
    .filter((s) => s.matched > 0)
    .sort((a, b) => b.matched - a.matched || a.dist - b.dist)
    .slice(0, n)
    .map((s) => s.item.name);
}

/** Build the not-found message, enriched with suggestions when we have close ones. */
export function notFoundMessage<T extends Named>(items: T[], term: string): string {
  const q = term.trim();
  const near = suggestNames(items, term, 3);
  if (!near.length) return `Couldn't find "${q}".`;
  if (near.length === 1) return `I don't see "${q}" — did you mean ${near[0]}?`;
  return `I don't see "${q}" — did you mean one of: ${near.join(", ")}?`;
}
